<template>
    <q-dialog v-model="showStaffHoursSpent" @hide="closeDialog()">
        <q-card :style="cardStyle">
            <q-card-section class="text-center">
                <h5>{{ staffRow.first_name }} {{ staffRow.last_name }}</h5>
            </q-card-section>
            <q-card-section>
                <q-form class="text-center" @submit.prevent="retrieveContactData">
                    <div class="contact-filter-group">
                        <div class="client-select">
                            <q-input class="client-select-input" outlined label-color="black" v-model="contactDateFrom"
                                mask="####-##-##" :placeholder="dateFormat" clearable label="Contact Date From" required
                                :rules="[(val) => (dayjs(val, dateFormat, true).isValid() || val == '') || 'Please enter a valid date']">
                                <template v-slot:append>
                                    <q-icon name="event" class="cursor-pointer">
                                        <q-popup-proxy transition-show="scale" transition-hide="scale">
                                            <q-date v-model="contactDateFrom" :mask="dateFormat" />
                                        </q-popup-proxy>
                                    </q-icon>
                                </template>
                            </q-input>
                        </div>

                        <div class="client-select">
                            <q-input class="client-select-input" outlined label-color="black" v-model="contactDateTo"
                                mask="####-##-##" :placeholder="dateFormat" clearable label="Contact Date To" required
                                :rules="[(val) => (dayjs(val, dateFormat, true).isValid() || val == '') || 'Please enter a valid date']">
                                <template v-slot:append>
                                    <q-icon name="event" class="cursor-pointer">
                                        <q-popup-proxy transition-show="scale" transition-hide="scale">
                                            <q-date v-model="contactDateTo" :mask="dateFormat" />
                                        </q-popup-proxy>
                                    </q-icon>
                                </template>
                            </q-input>
                        </div>
                    </div>
                    <div class="active-client-select">
                        <q-checkbox v-model="activeClients" label="Active Clients Only" />
                    </div>
                    <div class="descriptions-group q-mb-md">
                        <q-btn outline no-caps color="primary" :loading="retrievingContactData" label="Retrieve Data"
                            type="submit">
                            <q-tooltip class="text-body2">
                                Retrieve a list of interactions for the given staff member between the provided dates. The
                                previously set
                                filters are not applied
                            </q-tooltip>
                        </q-btn>                   
                    </div>
                    <div class="descriptions-group ">
                        <q-btn v-if="contactDescriptions" outline no-caps color="primary" label="View Interaction Breakdown" @click="showBreakdown = !showBreakdown">
                            <q-popup-proxy style="border-radius: 15px;">
                                <q-card style="min-width: 400px">
                                    <q-card-section>
                                        <strong>Programme Type Breakdown: {{ totalProgrammeTime }} hours </strong>
                                    </q-card-section>
                                    <q-card-section v-for="(description, i) in programmeChartData?.labels" :key="description">
                                        {{ description }}: {{ programmeChartData?.datasets[0].data[i] }} hours
                                    </q-card-section>
                                    <q-card-section>
                                        <strong>Contact Type Breakdown: {{ totalProgrammeTime }} hours </strong>
                                    </q-card-section>
                                    <q-card-section v-for="(description, i) in contactChartData?.labels" :key="description">
                                        {{ description }}: {{ contactChartData?.datasets[0].data[i] }} hours
                                    </q-card-section>
                                </q-card>
                            </q-popup-proxy>
                        </q-btn>    
                        <q-btn v-if="contactDescriptions" outline no-caps color="primary" label="View Contact Descriptions" @click="showDescriptions = !showDescriptions">
                            <q-popup-proxy style="border-radius: 15px;">
                                <q-card >
                                    <q-card-section v-for="description in contactDescriptions" :key="description">
                                        {{ description }}
                                    </q-card-section>
                                </q-card>
                            </q-popup-proxy>
                        </q-btn>                    
                    </div>
                </q-form>
            </q-card-section>
            <q-card-section v-if="retrievedContactData">
                <div class="interaction-graphs">
                    <div v-if="programmeChartData" class="graph-chart small-graph">
                        <Pie v-bind:chartData="this.programmeChartData" v-bind:chartOptions="programmeChartOptions" />
                    </div>
                    <div v-if="contactChartData" class="graph-chart small-graph">
                        <Pie v-bind:chartData="this.contactChartData" v-bind:chartOptions="contactChartOptions" />
                    </div>
                </div>
            </q-card-section>
            <q-card-section v-if="retrievedContactData">
                <TableFilterComponent :tableInformation="staffHoursSpent" :filterKeys="['Contact Type', 'Programme', 'Service']" @updateFilters="updateFilters" :key="staffHoursSpent"/>

                <q-table class="q-pa-md" :style="{ height: '800px' }" :rows="filteredRows" :columns="columns" :rows-per-page-options="[15]">
                    <template v-slot:top-right>
                        <q-btn no-caps fab-mini flat icon="download" @click="exportTable(filteredRows, `${staffRow.first_name} ${staffRow.last_name} - Interactions.csv`, { columns })" />
                    </template>

                    <template v-slot:body-cell="props">
                        <q-td :props="props">
                            <span v-if="props.col.name.includes('date')">
                                {{ props.value.split('T')[0] }}
                            </span>
                            <span v-else>
                                {{ props.value }}
                            </span>
                        </q-td>
                    </template>
                </q-table>
            </q-card-section>
        </q-card>
    </q-dialog>
</template>

<script>

import api from "../../../services/api/api"
import notify from '@/services/util/notify';
import dayjs from "../../../services/util/dayjs"


import Pie from "../../ChartComponents/Pie.vue";
import { retPieOptions } from "../../DataComponents/chartOptions"

import TableFilterComponent from '../TableFilterComponent.vue';

import { exportTable } from "../../../services/util/file";

export default {
    name: "ClientInteractionComponent",

    components: { Pie, TableFilterComponent },

    props: {
        staffRow: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            exportTable: exportTable,
            showDescriptions: false,
            showBreakdown: false,

            showStaffHoursSpent: false,
            staffHoursSpent:  {},
            columns: [],

            dayjs: dayjs,
            dateFormat: 'YYYY-MM-DD',

            retrievingContactData: false,
            retrievedContactData: false,
            contactDateFrom: '',
            contactDateTo: '',

            cardWidth: '50vw',
            activeClients: true,

            selectedFilters: {},

            contactDescriptions: null,

            programmeChartData: null,
            contactChartData: null,

            programmeChartOptions: retPieOptions("Interaction Time By Programme (Hours)", "Programme"),
            contactChartOptions: retPieOptions("Interaction Time By Contact Type (Hours)", "Contact Type"),

        }
    },

    computed: {

        /**
         * Set the style of the card style when width changes
         */
        cardStyle() {
            return `min-width: ${this.cardWidth}; min-height: 100px`;
        },


        /**
         * Generate filtered rows by applying selected filters to staff data
         */
        filteredRows() {
            if (this.staffHoursSpent.length == 0) {
                return this.staffHoursSpent;
            }

            return this.staffHoursSpent.filter((row) => {
                return Object.keys(this.selectedFilters).every((key) => {

                    if (this.selectedFilters[key].length === 0) {
                        return true;
                    }
                    return this.selectedFilters[key].includes(row[key]);
                });
            });
        },

        totalProgrammeTime() {
            if (this.programmeChartData) {
                return this.programmeChartData?.datasets[0].data.reduce((total, num) => total + Number(num), 0);
            } 
            return 0
        },

        totalContactTime() {
            if (this.contactChartData) {
                return this.contactChartData?.datasets[0].data.reduce((total, num) => total + Number(num), 0);
            } 
            return 0
        }
    },


    created() {
        this.showStaffHoursSpent = true;

        const today = new Date();
        const lastMonth = new Date(today);
        lastMonth.setMonth(today.getMonth() - 1);

        this.contactDateTo = today.toISOString().split('T')[0];
        this.contactDateFrom = lastMonth.toISOString().split('T')[0];

        this.retrieveContactData();
    },

    methods: {

        /**
         * Retrieves contact data for a given staff member between dates.
         */
        async retrieveContactData() {
            const filters = {};

            if (this.contactDateFrom) { filters.contactDateFrom = this.contactDateFrom }
            if (this.contactDateTo) { filters.contactDateTo = this.contactDateTo }

            if (this.activeClients) {
                filters.status_id = 'Active'
            }

            const contactQuery = "?filters=" + encodeURIComponent(JSON.stringify(filters));

            this.retrievingContactData = true

            api.client.getHoursSpentByStaffId(this.staffRow.email, contactQuery).then((res) => {
                this.staffHoursSpent = res.data.tableRows;
                this.columns = res.data.tableColumns;
                this.contactDescriptions = res.data.contactDescriptions;
                this.programmeChartData = res.data.pieData[0];
                this.contactChartData = res.data.pieData[1];
                this.retrievedContactData = true;
                this.cardWidth = '80vw';
            }).catch((error) => {
                notify.error(error.message, 'top');
            }).finally(() => {
                this.retrievingContactData = false
            })
        },


        updateFilters(filters) {
            this.selectedFilters = filters;
        },


        /**
         * Resets the cards appearance and default values.
         */
        closeDialog() {
            this.cardWidth = '50vw';
            this.activeClients = true;
            this.$emit('close')
        }
    }
}

</script>

<style></style>
